import React, {
  createContext,
  useState,
  useRef,
  useContext,
  useEffect,
  useCallback,
} from "react";

const VideoContext = createContext();

export const useVideo = () => useContext(VideoContext);

export const VideoProvider = ({ children }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  // حالت‌های اصلی ویدیو
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [videoFile, setVideoFile] = useState(null);
  const [videoSrc, setVideoSrc] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  // زیرنویس‌ها
  const [subtitle1, setSubtitle1] = useState({ x: 0.5, y: 0.75 });
  const [subtitle2, setSubtitle2] = useState({ x: 0.5, y: 0.85 });
  const [subtitle1Text, setSubtitle1Text] = useState("");
  const [subtitle2Text, setSubtitle2Text] = useState("");
  const [subtitle1Loaded, setSubtitle1Loaded] = useState(false);
  const [subtitle2Loaded, setSubtitle2Loaded] = useState(false);
  const [subtitle1Json, setSubtitle1Json] = useState([]);
  const [subtitle2Json, setSubtitle2Json] = useState([]);

  // حالت‌های UI
  const [isDraggingSub, setIsDraggingSub] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showSpeedDial, setShowSpeedDial] = useState(false);
  const [isHoveringSub1, setIsHoveringSub1] = useState(false);
  const [isHoveringSub2, setIsHoveringSub2] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [controlsAutoHide, setControlsAutoHide] = useState(true);
  const [hideControlsDelay, setHideControlsDelay] = useState(3000);

  // stateهای جدید برای مدیریت لودینگ
  const [isLoadingSubtitles, setIsLoadingSubtitles] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  // stateهای جدید برای مدیریت مدال زیرنویس
  const [showSubtitleNotFoundModal, setShowSubtitleNotFoundModal] =
    useState(false);
  const [currentMovieName, setCurrentMovieName] = useState("");
  const [showOtherUsersSubtitles, setShowOtherUsersSubtitles] = useState(false);
  const [otherUsersSubtitlesData, setOtherUsersSubtitlesData] = useState(null);
  const [videoMetadata, setVideoMetadata] = useState({
    videoType: "", // 'movie' یا 'series'
    filmName: "", // نام فیلم یا سریال
    fileName: "", // نام فایل اصلی
    season: "", // شماره فصل (برای سریال)
    episode: "", // شماره قسمت (برای سریال)
  });

  // ردیابی آی‌دی‌های زیرنویس لود اتوماتیک (برای نمایش آیکون ذخیره)
  const [autoLoadedSub1Id, setAutoLoadedSub1Id] = useState(null);
  const [autoLoadedSub2Id, setAutoLoadedSub2Id] = useState(null);

  // راهنمای کاربر
  const [showTutorialGuide, setShowTutorialGuide] = useState(false);

  // در بخش stateها (بعد از stateهای زیرنویس)
  const [subtitle1Delay, setSubtitle1Delay] = useState(0); // به میلی‌ثانیه
  const [subtitle2Delay, setSubtitle2Delay] = useState(0); // به میلی‌ثانیه
  const [showDelayBox, setShowDelayBox] = useState(false);
  const [delayBoxTimeout, setDelayBoxTimeout] = useState(null);

  // در بخش stateها، این state جدید رو اضافه کنید (ترجیحاً بعد از stateهای UI)
  const [shortcutsEnabled, setShortcutsEnabled] = useState(true);

  // تشخیص iOS
  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);
  }, []);

  // توابع کنترلی
  const playVideo = () => {
    videoRef.current?.play();
    setIsPlaying(true);
  };

  const pauseVideo = () => {
    videoRef.current?.pause();
    setIsPlaying(false);
  };

  const togglePlay = () => (isPlaying ? pauseVideo() : playVideo());

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current?.currentTime || 0);
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current?.duration || 0);
  };

  const handleSeek = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (newVolume) => {
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      if (isMuted) {
        videoRef.current.volume = volume || 0.5;
        setIsMuted(false);
      } else {
        videoRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  const changePlaybackRate = (rate) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = rate;
      setPlaybackRate(rate);
    }
  };

  const handleFileSelect = (file) => {
    if (file?.type.startsWith("video/")) {
      setVideoFile(file);
      setVideoSrc(URL.createObjectURL(file));
    }
  };

  // VideoContext.jsx - iOS fake fullscreen with proper scroll prevention
  const toggleFullscreen = useCallback(async () => {
    if (isIOS) {
      setIsFullscreen(!isFullscreen);

      if (!isFullscreen) {
        const scrollY = window.scrollY;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.body.style.left = "0";
        document.body.dataset.scrollY = scrollY;
      } else {
        const scrollY = parseInt(document.body.dataset.scrollY || "0", 10);
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.left = "";
        delete document.body.dataset.scrollY;
        window.scrollTo(0, scrollY);
      }
      return;
    } else {
      if (!isFullscreen) {
        const element = containerRef.current;
        if (element) {
          if (element.requestFullscreen) {
            await element.requestFullscreen();
          } else if (element.webkitRequestFullscreen) {
            await element.webkitRequestFullscreen();
          }
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        }
      }
    }
  }, [isIOS, isFullscreen, containerRef]);

  // Cleanup iOS fake fullscreen on unmount
  useEffect(() => {
    return () => {
      if (document.body.style.position === "fixed") {
        const scrollY = parseInt(document.body.dataset.scrollY || "0", 10);
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        document.body.style.left = "";
        delete document.body.dataset.scrollY;
        window.scrollTo(0, scrollY);
      }
    };
  }, []);

  // تنظیم موقعیت پیش‌فرض زیرنویس‌ها
  const resetSubtitlePositions = () => {
    setSubtitle1({ x: 0.5, y: 0.75 });
    setSubtitle2({ x: 0.5, y: 0.85 });
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const removeSubtitle = useCallback(
    (subtitleNumber) => {
      if (!videoRef.current) return;

      const trackId = `subtitle-track-${subtitleNumber}`;
      const track = document.getElementById(trackId);

      if (track && track.parentNode === videoRef.current) {
        videoRef.current.removeChild(track);
      }

      if (subtitleNumber === 1) {
        setSubtitle1Text("");
        setSubtitle1Loaded(false);
        setSubtitle1Json([]);
      } else {
        setSubtitle2Text("");
        setSubtitle2Loaded(false);
        setSubtitle2Json([]);
      }
    },
    [videoRef],
  );

  const value = {
    // رفرنس‌ها
    videoRef,
    containerRef,

    // وضعیت‌های پایه
    isPlaying,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackRate,
    isFullscreen,
    showSubtitles,
    videoFile,
    videoSrc,

    // زیرنویس‌ها
    subtitle1,
    subtitle2,
    subtitle1Text,
    subtitle2Text,
    subtitle1Loaded,
    subtitle2Loaded,
    subtitle1Json,
    subtitle2Json,

    // وضعیت‌های UI
    isDraggingSub,
    showSettings,
    showSpeedDial,
    isHoveringSub1,
    isHoveringSub2,
    isIOS,
    controlsAutoHide,
    hideControlsDelay,
    isModalOpen,
    setIsModalOpen,

    // stateهای جدید لودینگ
    isLoadingSubtitles,
    loadingMessage,

    // Setters
    setIsPlaying,
    setCurrentTime,
    setDuration,
    setVolume,
    setIsMuted,
    setPlaybackRate,
    setIsFullscreen,
    setShowSubtitles,
    setVideoFile,
    setVideoSrc,
    setSubtitle1,
    setSubtitle2,
    setSubtitle1Text,
    setSubtitle2Text,
    setSubtitle1Loaded,
    setSubtitle2Loaded,
    setSubtitle1Json,
    setSubtitle2Json,
    setIsDraggingSub,
    setShowSettings,
    setShowSpeedDial,
    setIsHoveringSub1,
    setIsHoveringSub2,
    setControlsAutoHide,
    setHideControlsDelay,

    // setters جدید
    setIsLoadingSubtitles,
    setLoadingMessage,

    // توابع
    playVideo,
    pauseVideo,
    togglePlay,
    handleSeek,
    handleVolumeChange,
    toggleMute,
    changePlaybackRate,
    handleFileSelect,
    toggleFullscreen,
    handleTimeUpdate,
    handleLoadedMetadata,
    resetSubtitlePositions,
    removeSubtitle,
    showSubtitleNotFoundModal,
    setShowSubtitleNotFoundModal,
    currentMovieName,
    setCurrentMovieName,
    showOtherUsersSubtitles,
    setShowOtherUsersSubtitles,
    otherUsersSubtitlesData,
    setOtherUsersSubtitlesData,
    shortcutsEnabled,
    setShortcutsEnabled,
    subtitle1Delay,
    subtitle2Delay,
    setSubtitle1Delay,
    setSubtitle2Delay,
    showDelayBox,
    setShowDelayBox,
    delayBoxTimeout,
    setDelayBoxTimeout,
    videoMetadata,
    setVideoMetadata,
    autoLoadedSub1Id,
    setAutoLoadedSub1Id,
    setAutoLoadedSub2Id,
    showTutorialGuide,
    setShowTutorialGuide,
  };

  return (
    <VideoContext.Provider value={value}>{children}</VideoContext.Provider>
  );
};
